import { db, storage } from '../../../integrations/firebase';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { recordTimelineEvent } from '../../../core/customer/CustomerTimelineEngine';
import { ref, set, remove, update, get, push, query, orderByKey, orderByChild, equalTo, startAt, endAt, limitToFirst, runTransaction } from 'firebase/database';
import { normalizeMobile } from '../../../shared/utils/formatters';
import { referralRef, referralGet } from '../../../integrations/firebase/referralDb';
import { buildOrderSnapshot } from '../../../core/customer/orderSnapshot';
import { recordRead, approxBytes } from '../../../integrations/rtdbMeter';

const BATCH_SIZE = 1000;
const CUSTOMER_CACHE = new Map(); // In-memory cache for 0.001s access


const PROXY_BASE = (import.meta.env?.VITE_PROXY_URL || (import.meta.env?.DEV ? 'http://localhost:3001' : '')).replace(/\/$/, '');

/**
 * Server-side customer query — filters run over the WHOLE base.
 *
 * The list used to fetch 50 records and filter those 50 in the browser, so a filter
 * like "10+ orders" searched 50 of 111,875 customers and showed 0 results. The proxy
 * holds a compact index of every customer and returns the matching page plus the true
 * match count.
 */
export const queryCustomers = async (filters = {}, page = 1, limit = 50, sort = 'mobile', dir = 'desc') => {
    const { authHeaders } = await import('../../../core/api/authHeaders');
    const params = new URLSearchParams();
    Object.entries({ ...filters, page, limit, sort, dir }).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '' && v !== 'all') params.set(k, String(v));
    });
    const r = await fetch(`${PROXY_BASE}/api/customers/query?${params.toString()}`, { headers: await authHeaders() });
    const j = await r.json().catch(() => ({}));
    if (!r.ok || !j.ok) throw new Error(j.error || `Customer query failed (${r.status})`);

    // The index uses short field names to keep 111k rows small on the wire; expand them
    // into what the table columns read.
    return {
        total: j.total, pages: j.pages, page: j.page, indexedAt: j.indexedAt, indexSize: j.indexSize,
        rows: (j.rows || []).map(x => ({
            id: x.k, firebaseKey: x.k, primaryMobile: x.k,
            name: x.n, city: x.c, state: x.st,
            totalOrders: x.o, totalSpent: x.s, totalRevenue: x.s,
            healthScore: x.h, churnRisk: x.r, customerType: x.t,
            lastOrderDate: x.l, firstOrderDate: x.f,
            addressCount: x.a, relationshipAgeDays: x.cm,
            doNotShip: x.d ? { flagged: true } : null, dnd: !!x.dn,
        })),
    };
};

/** Download the full filtered customer list as CSV, streamed by the proxy. */
export const exportCustomersCsv = async (filters = {}) => {
    const { authHeaders } = await import('../../../core/api/authHeaders');
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '' && v !== 'all') params.set(k, String(v));
    });
    const r = await fetch(`${PROXY_BASE}/api/customers/export?${params.toString()}`, { headers: await authHeaders() });
    if (!r.ok) {
        // The server meters customer downloads per login per day and may refuse (429)
        // or deny the role (403); show its message rather than saving an error as a file.
        const msg = await r.json().then(j => j.error).catch(() => null);
        throw new Error(msg || `Export failed (${r.status})`);
    }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `customers-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 30000);
};

/** Existing campaigns (for the "add to campaign" picker). */
export const listCampaigns = async () => {
    const { authHeaders } = await import('../../../core/api/authHeaders');
    const r = await fetch(`${PROXY_BASE}/api/analytics/campaign`, { headers: await authHeaders() });
    const j = await r.json().catch(() => ({}));
    if (!r.ok || !j.ok) throw new Error(j.error || `Could not load campaigns (${r.status})`);
    return j.campaigns || [];
};

/**
 * Add the currently-filtered customers to a campaign as the NEXT BATCH.
 * Pass an existing `campaignId`, or a `name` to create a new campaign.
 * Returns the dedupe report (added / already-in-batch-N / in another campaign).
 */
export const addToCampaign = async ({ campaignId = '', name = '', filters = {}, mobiles, source = 'customer-intelligence' } = {}) => {
    const { authHeaders } = await import('../../../core/api/authHeaders');
    const clean = {};
    Object.entries(filters || {}).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '' && v !== 'all') clean[k] = v; });
    const r = await fetch(`${PROXY_BASE}/api/campaigns/add`, {
        method: 'POST', headers: { ...(await authHeaders()), 'Content-Type': 'application/json' },
        body: JSON.stringify({ campaignId, name, source, ...(mobiles ? { mobiles } : { filters: clean }) }),
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok || !j.ok) throw new Error(j.error || `Add to campaign failed (${r.status})`);
    return j;
};

export const CustomerService = {
    addToCampaign,
    listCampaigns,
    // --- CACHE MANAGEMENT ---
    getFromCache: (key) => CUSTOMER_CACHE.get(key),
    setInCache: (key, value) => CUSTOMER_CACHE.set(key, value),
    clearCache: () => {
        CUSTOMER_CACHE.clear();
        CustomerService.lastFetchAllTime = null;
    },
    lastFetchAllTime: null,

    // --- SEARCH (OPTIMIZED) ---
    searchCustomers: async (queryText) => {
        if (!queryText) return [];
        const cleanQuery = queryText.trim().toLowerCase();

        // 1. Mobile Number Search (Direct Key Lookup - O(1))
        if (/^\d+$/.test(cleanQuery.replace(/\D/g, ''))) {
            const normalized = normalizeMobile(cleanQuery);
            const safeKey = normalized.replace(/[.#$[\]/]/g, '_');
            // Stub records (website-pushed) may lack primaryMobile — ALWAYS fill
            // id + mobile from the key, whether the record came from cache or DB,
            // so the row shows its number and is clickable.
            const enrich = (data) => ({ id: safeKey, ...data, primaryMobile: data.primaryMobile || data.mobile || normalized });
            if (CUSTOMER_CACHE.has(safeKey)) {
                return [enrich(CUSTOMER_CACHE.get(safeKey))];
            }

            // Fetch from DB
            const snapshot = await get(ref(db, `customerBase/${safeKey}`));
            if (snapshot.exists()) {
                const row = enrich(snapshot.val());
                CUSTOMER_CACHE.set(safeKey, row); // Cache it
                return [row];
            }
            // Fallback: If direct lookup fails (e.g. partial number), fall through to full search
        }

        // 2. Name / partial search — SERVER-SIDE.
        //
        // This used to call fetchAllCustomers(), which downloads the ENTIRE
        // customerBase tree (~180MB, 111k customers) and filtered it in the
        // browser. The search box sits in the global header, so one agent
        // typing a customer's name pulled 180MB of billed egress per search.
        // The proxy already keeps a compact in-memory index for exactly this.
        try {
            const { rows } = await queryCustomers({ search: cleanQuery }, 1, 25);
            return rows;
        } catch (error) {
            console.error('Search failed', error);
            return [];
        }
    },

    // Batch Upload
    uploadToFirebaseInBatches: async (customers, onProgress) => {
        const total = customers.length;
        let processed = 0;

        // Update total count first (optimistic)
        try {
            const { increment } = await import('firebase/database');
            await update(ref(db, 'stats'), {
                customerCount: increment(total)
            });
        } catch (e) {
            console.warn("Failed to update stats counter", e);
        }

        for (let i = 0; i < total; i += BATCH_SIZE) {
            const batch = customers.slice(i, i + BATCH_SIZE);
            const updates = {};

            batch.forEach(customer => {
                // Sanitize key (Firebase doesn't allow ., $, #, [, ], /)
                const safeKey = customer.primaryMobile.replace(/[.#$[\]/]/g, '_');
                updates[`customerBase/${safeKey}`] = customer;
                CUSTOMER_CACHE.set(safeKey, customer); // Populate Cache
            });

            try {
                await update(ref(db), updates);
                processed += batch.length;
                if (onProgress) onProgress(Math.round((processed / total) * 100));

                // Small delay to prevent rate limiting
                await new Promise(resolve => setTimeout(resolve, 100));
            } catch (error) {
                console.error("Batch Upload Error:", error);
                throw error; // Let the caller handle retry
            }
        }
    },

    // Delete Single Customer
    deleteCustomer: async (mobile) => {
        const safeKey = mobile.replace(/[.#$[\]/]/g, '_');
        await remove(ref(db, `customerBase/${safeKey}`));
        CUSTOMER_CACHE.delete(safeKey);
    },

    // Wipe Database
    wipeCustomerDatabase: async () => {
        await remove(ref(db, 'customerBase'));
        await set(ref(db, 'stats/customerCount'), 0);
        CUSTOMER_CACHE.clear();
        CustomerService.lastFetchAllTime = null;
    },

    // --- ADDRESSES ---
    updateAddress: async (mobile, addressIndex, addressData) => {
        const safeKey = mobile.replace(/[.#$[\]/]/g, '_');
        await update(ref(db, `customerBase/${safeKey}/addresses/${addressIndex}`), addressData);
        // Update cache if exists
        if (CUSTOMER_CACHE.has(safeKey)) {
            const cached = CUSTOMER_CACHE.get(safeKey);
            // Create a new array reference for addresses to ensure React detects change
            const newAddresses = [...(cached.addresses || [])];
            newAddresses[addressIndex] = { ...newAddresses[addressIndex], ...addressData };

            const newCached = { ...cached, addresses: newAddresses };
            CUSTOMER_CACHE.set(safeKey, newCached);
        }
    },

    /**
     * Update the editable identity fields on a customer.
     *
     * Writes ONLY the fields passed in, and records what changed on the timeline — a
     * customer record is read by invoices, labels and campaigns, so "who changed this
     * address and when" has to have an answer. The caller validates first
     * (logic/customerProfileFields); this trusts what it is given.
     */
    updateProfile: async (mobile, fields, { actor = 'staff', changes = null } = {}) => {
        const safeKey = mobile.replace(/[.#$[\]/]/g, '_');
        const patch = { ...fields, updatedAt: Date.now() };
        await update(ref(db, `customerBase/${safeKey}`), patch);

        if (CUSTOMER_CACHE.has(safeKey)) {
            CUSTOMER_CACHE.set(safeKey, { ...CUSTOMER_CACHE.get(safeKey), ...patch });
        }
        if (changes && Object.keys(changes).length) {
            await recordTimelineEvent(mobile, 'profile_updated', {
                summary: Object.keys(changes).join(', '),
                changes,
            }, actor);
        }
        return patch;
    },

    /**
     * Replace a customer's photograph.
     *
     * Stored under a path keyed by the customer, with the old object deleted afterwards
     * so a profile photographed five times does not leave five files behind. A failed
     * cleanup never fails the upload — the new photo is already live.
     */
    uploadPhoto: async (mobile, file, { actor = 'staff', previousPath = '' } = {}) => {
        const safeKey = mobile.replace(/[.#$[\]/]/g, '_');
        const ext = (String(file.type).split('/')[1] || 'jpg').replace(/[^a-z0-9]/gi, '');
        const path = `customerPhotos/${safeKey}/${Date.now()}.${ext}`;
        await uploadBytes(storageRef(storage, path), file, { contentType: file.type });
        const photoUrl = await getDownloadURL(storageRef(storage, path));

        await CustomerService.updateProfile(mobile, { photoUrl, photoPath: path }, { actor });
        await recordTimelineEvent(mobile, 'profile_photo_updated', { summary: 'Photograph updated' }, actor);

        if (previousPath && previousPath !== path) {
            try { await deleteObject(storageRef(storage, previousPath)); }
            catch (e) { console.warn('[CustomerService] old photo not removed:', e.message); }
        }
        return { photoUrl, photoPath: path };
    },

    removePhoto: async (mobile, { actor = 'staff', previousPath = '' } = {}) => {
        const safeKey = mobile.replace(/[.#$[\]/]/g, '_');
        await update(ref(db, `customerBase/${safeKey}`), { photoUrl: null, photoPath: null, updatedAt: Date.now() });
        if (CUSTOMER_CACHE.has(safeKey)) {
            const c = { ...CUSTOMER_CACHE.get(safeKey) };
            delete c.photoUrl; delete c.photoPath;
            CUSTOMER_CACHE.set(safeKey, c);
        }
        await recordTimelineEvent(mobile, 'profile_photo_updated', { summary: 'Photograph removed' }, actor);
        if (previousPath) {
            try { await deleteObject(storageRef(storage, previousPath)); }
            catch (e) { console.warn('[CustomerService] photo file not removed:', e.message); }
        }
    },

    /**
     * Delete one saved address.
     *
     * Addresses are stored as an ARRAY, so removing index 1 of 3 must rewrite the whole
     * list — splicing the node in place would leave a hole that reads back as a null
     * entry and breaks every address picker.
     */
    deleteAddress: async (mobile, addressIndex, { actor = 'staff' } = {}) => {
        const safeKey = mobile.replace(/[.#$[\]/]/g, '_');
        const addressesRef = ref(db, `customerBase/${safeKey}/addresses`);
        const snapshot = await get(addressesRef);
        const current = snapshot.val() || [];
        const list = (Array.isArray(current) ? current : Object.values(current)).filter(Boolean);
        if (addressIndex < 0 || addressIndex >= list.length) return;
        const removed = list[addressIndex];
        const next = list.filter((_, i) => i !== addressIndex);

        await set(addressesRef, next);
        if (CUSTOMER_CACHE.has(safeKey)) {
            CUSTOMER_CACHE.set(safeKey, { ...CUSTOMER_CACHE.get(safeKey), addresses: next });
        }
        await recordTimelineEvent(mobile, 'address_removed', {
            summary: String(removed?.fullAddress || removed?.address || '').slice(0, 120),
        }, actor);
    },

    /** Move one address to the front — the ERP treats the first as primary. */
    setPrimaryAddress: async (mobile, addressIndex, { actor = 'staff' } = {}) => {
        const safeKey = mobile.replace(/[.#$[\]/]/g, '_');
        const addressesRef = ref(db, `customerBase/${safeKey}/addresses`);
        const snapshot = await get(addressesRef);
        const current = snapshot.val() || [];
        const list = (Array.isArray(current) ? current : Object.values(current)).filter(Boolean);
        if (addressIndex <= 0 || addressIndex >= list.length) return;
        const next = [list[addressIndex], ...list.filter((_, i) => i !== addressIndex)];

        await set(addressesRef, next);
        if (CUSTOMER_CACHE.has(safeKey)) {
            CUSTOMER_CACHE.set(safeKey, { ...CUSTOMER_CACHE.get(safeKey), addresses: next });
        }
        await recordTimelineEvent(mobile, 'address_updated', { summary: 'Primary delivery address changed' }, actor);
    },

    addNewAddress: async (mobile, addressData) => {
        const safeKey = mobile.replace(/[.#$[\]/]/g, '_');
        const addressesRef = ref(db, `customerBase/${safeKey}/addresses`);
        // Get current addresses to find next index or push
        const snapshot = await get(addressesRef);
        const currentAddresses = snapshot.val() || [];
        const nextIndex = currentAddresses.length;

        await update(ref(db, `customerBase/${safeKey}/addresses/${nextIndex}`), addressData);

        // Update cache
        if (CUSTOMER_CACHE.has(safeKey)) {
            const cached = CUSTOMER_CACHE.get(safeKey);
            const newAddresses = [...(cached.addresses || []), addressData];
            const newCached = { ...cached, addresses: newAddresses };
            CUSTOMER_CACHE.set(safeKey, newCached);
        }
    },

    // --- REMARKS ---
    addRemark: async (mobile, remark) => {
        const safeKey = mobile.replace(/[.#$[\]/]/g, '_');
        const newRemarkRef = push(ref(db, `customerBase/${safeKey}/remarks`));
        const remarkData = {
            ...remark,
            createdAt: new Date().toISOString()
        };
        await set(newRemarkRef, remarkData);

        // Update cache
        if (CUSTOMER_CACHE.has(safeKey)) {
            const cached = CUSTOMER_CACHE.get(safeKey);
            const newRemarks = { ...(cached.remarks || {}), [newRemarkRef.key]: remarkData };
            CUSTOMER_CACHE.set(safeKey, { ...cached, remarks: newRemarks });
        }

        return newRemarkRef.key;
    },

    deleteRemark: async (mobile, remarkId) => {
        const safeKey = mobile.replace(/[.#$[\]/]/g, '_');
        await remove(ref(db, `customerBase/${safeKey}/remarks/${remarkId}`));

        // Update cache
        if (CUSTOMER_CACHE.has(safeKey)) {
            const cached = CUSTOMER_CACHE.get(safeKey);
            if (cached.remarks) {
                const newRemarks = { ...cached.remarks };
                delete newRemarks[remarkId];
                CUSTOMER_CACHE.set(safeKey, { ...cached, remarks: newRemarks });
            }
        }
    },

    // --- COMPLAINTS ---
    addComplaint: async (mobile, complaint) => {
        const safeKey = mobile.replace(/[.#$[\]/]/g, '_');

        // Auto-Classify
        let category = complaint.category || 'General';
        const detailsLower = (complaint.details || '').toLowerCase();
        if (detailsLower.includes('leak') || detailsLower.includes('broken') || detailsLower.includes('damage')) category = 'Product Issue';
        else if (detailsLower.includes('late') || detailsLower.includes('delay') || detailsLower.includes('courier')) category = 'Courier Delay';
        else if (detailsLower.includes('wrong') || detailsLower.includes('missing')) category = 'Order Error';

        const newComplaintRef = push(ref(db, `customerBase/${safeKey}/complaints`));
        await set(newComplaintRef, {
            ...complaint,
            category,
            status: 'Open',
            createdAt: new Date().toISOString(),
            lastUpdatedAt: new Date().toISOString()
        });
        return newComplaintRef.key;
    },

    updateComplaint: async (mobile, complaintId, updates) => {
        const safeKey = mobile.replace(/[.#$[\]/]/g, '_');
        await update(ref(db, `customerBase/${safeKey}/complaints/${complaintId}`), {
            ...updates,
            lastUpdatedAt: new Date().toISOString()
        });
    },

    // --- ORDERS ---
    addOrder: async (mobile, orderData) => {
        const safeKey = mobile.replace(/[.#$[\]/]/g, '_');
        const { items, ...orderDetails } = orderData;

        // 1. Deduct Stock (if items present)
        if (items && items.length > 0) {
            for (const item of items) {
                if (item.sku) {
                    const stockRef = ref(db, `products/${item.sku}/stock`);
                    await runTransaction(stockRef, (currentStock) => {
                        if (currentStock === null) return 0; // Or handle error
                        if (currentStock < item.qty) throw new Error(`Insufficient stock for ${item.name}`);
                        return currentStock - item.qty;
                    });
                }
            }
        }

        const timestamp = new Date().toISOString();
        const orderId = orderDetails.orderId || `ORD-${Date.now()}`;

        const fullOrder = {
            ...orderDetails,
            id: orderId,
            items: items || [],
            customer: { mobile: mobile }, // Minimal customer info for global list
            status: 'Pending',
            createdAt: timestamp,
            orderDate: orderDetails.orderDate || timestamp.split('T')[0]
        };

        // 2. Add to Global Orders (for Order List visibility)
        const globalOrderRef = push(ref(db, 'orders'));
        await set(globalOrderRef, {
            ...fullOrder,
            firebaseKey: globalOrderRef.key,
            // Add customer details needed for Order List
            customer: {
                mobile: mobile,
                name: 'Customer', // ideally fetch name
                address: 'From Profile'
            }
        });

        // 3. Add to Customer Profile (Local)
        const newOrderRef = push(ref(db, `customerBase/${safeKey}/orders`));
        await set(newOrderRef, {
            ...fullOrder,
            firebaseKey: newOrderRef.key
        });

        // 4. Update Customer Stats
        const { increment } = await import('firebase/database');
        await update(ref(db, `customerBase/${safeKey}`), {
            totalOrders: increment(1),
            totalSpent: increment(Number(orderDetails.amount) || 0),
            lastOrderDate: timestamp
        });

        // Update Cache
        if (CUSTOMER_CACHE.has(safeKey)) {
            const cached = CUSTOMER_CACHE.get(safeKey);
            const newOrders = [...(cached.orders || []), { ...fullOrder, firebaseKey: newOrderRef.key }];
            // Sort by date desc
            newOrders.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

            CUSTOMER_CACHE.set(safeKey, {
                ...cached,
                orders: newOrders,
                totalOrders: (cached.totalOrders || 0) + 1,
                totalSpent: (cached.totalSpent || 0) + (Number(orderDetails.amount) || 0),
                lastOrderDate: timestamp
            });
        }

        return newOrderRef.key;
    },

    // --- SAVED FILTERS ---
    saveFilterPreset: async (userId, filterName, filterConfig) => {
        const safeUser = userId.replace(/[.#$[\]/]/g, '_');
        await set(ref(db, `userFilters/${safeUser}/${filterName}`), filterConfig);
    },

    getFilterPresets: async (userId) => {
        const safeUser = userId.replace(/[.#$[\]/]/g, '_');
        const snapshot = await get(ref(db, `userFilters/${safeUser}`));
        return snapshot.exists() ? snapshot.val() : {};
    },

    // --- FETCHING ---
    // Fetch All (Paginated fetch would be better for real app, but for now fetching all for analytics)
    // REMOVED FROM USE. This read the ENTIRE customerBase tree (~177 MB / 111k
    // customers) and was billed as egress on every call — the largest single line on
    // the October 2026 Realtime Database bill. Customer Analytics, its only caller,
    // now aggregates on the server (`GET /api/analytics/customers`).
    //
    // It is kept behind a hard refusal rather than deleted so that a forgotten or
    // newly written caller FAILS LOUDLY instead of quietly costing ₹15 a click.
    // For lists and search use queryCustomers(); for one customer use getCustomer().
    fetchAllCustomers: async () => {
        throw new Error(
            'fetchAllCustomers() is disabled: it downloads ~177 MB of customerBase. '
            + 'Use GET /api/analytics/customers for aggregates, queryCustomers() for lists, '
            + 'or getCustomer(mobile) for one record.'
        );
    },

    /** The old implementation, retained for reference only. Not reachable. */
    _fetchAllCustomersLegacy: async () => {
        // Check if we have a valid full cache
        const now = Date.now();
        if (CustomerService.lastFetchAllTime && (now - CustomerService.lastFetchAllTime < 5 * 60 * 1000)) {
            // Return cached values if less than 5 minutes old
            return Array.from(CUSTOMER_CACHE.values());
        }

        const snapshot = await get(ref(db, 'customerBase'));
        // The single most expensive read in the app — always metered, so the
        // Data Usage panel shows exactly which screen triggered it.
        try { recordRead('/customerBase (FULL TREE)', approxBytes(snapshot.val())); } catch { /* ignore */ }
        if (snapshot.exists()) {
            const val = snapshot.val();
            const customers = val && typeof val === 'object' ? Object.values(val) : [];

            // Update Cache
            customers.forEach(c => {
                if (c.primaryMobile) {
                    const key = c.primaryMobile.replace(/[.#$[\]/]/g, '_');
                    CUSTOMER_CACHE.set(key, c);
                }
            });
            CustomerService.lastFetchAllTime = now;

            return customers;
        }
        return [];
    },

    // Fetch Paginated Customers
    fetchCustomersPaginated: async (lastKey = null, limit = 50) => {
        let customerRef = ref(db, 'customerBase');
        let queryRef;

        if (lastKey) {
            queryRef = query(customerRef, orderByKey(), startAt(lastKey), limitToFirst(limit + 1));
        } else {
            queryRef = query(customerRef, orderByKey(), limitToFirst(limit));
        }

        const snapshot = await get(queryRef);
        if (snapshot.exists()) {
            const data = snapshot.val();
            if (!data || typeof data !== 'object') return [];

            const sorted = Object.entries(data).map(([key, value]) => {
                CUSTOMER_CACHE.set(key, value); // Cache every fetched item
                // Stubs may lack primaryMobile — the key IS the 10-digit mobile.
                return { ...value, firebaseKey: key, id: key, primaryMobile: value.primaryMobile || value.mobile || key };
            });

            if (lastKey && sorted[0].firebaseKey === lastKey) {
                sorted.shift();
            }

            return sorted;
        }
        return [];
    },

    // Get Single Customer
    getCustomer: async (mobile) => {
        const normalized = normalizeMobile(mobile);
        const safeKey = normalized.replace(/[.#$[\]/]/g, '_');

        // 1. Check Cache
        if (CUSTOMER_CACHE.has(safeKey)) {
            return CUSTOMER_CACHE.get(safeKey);
        }

        // Build a profile (name/address/orders/stats) from this number's orders.
        // Used for customers that exist in orders but were never fully synced to
        // customerBase (e.g. website-pushed orders). Read-only.
        // SERVER-SIDE over the indexed customer/mobile child: this used to pull
        // the WHOLE orders tree into the browser on every sparse-profile open —
        // the single biggest hidden RTDB download in the app, and the stall the
        // desk felt as "Loading Profile…".
        const loadOrdersProfile = async () => {
            const { authHeaders } = await import('../../../core/api/authHeaders');
            const r = await fetch(`${PROXY_BASE}/api/customers/profile-orders?mobile=${normalized}`, { headers: await authHeaders() });
            const j = await r.json().catch(() => ({}));
            const mine = (r.ok && j.ok && Array.isArray(j.orders)) ? j.orders : [];
            if (!mine.length) return null;
            mine.sort((a, b) => new Date(b.createdAt || b.date || 0) - new Date(a.createdAt || a.date || 0));
            const latest = mine[0].customer || {};
            const totalSpent = mine.reduce((s, o) => s + (Number(o.payment?.total || o.totalAmount || 0)), 0);
            const orders = mine.map(o => buildOrderSnapshot(o, o.firebaseKey));
            return {
                primaryMobile: normalized, name: latest.name || 'Customer',
                city: latest.city || '', state: latest.state || '', address: latest.address || '', pincode: latest.pincode || '',
                totalOrders: mine.length, totalSpent, orders,
                firstOrderDate: mine[mine.length - 1].createdAt || mine[mine.length - 1].date || '',
                lastOrderDate: mine[0].createdAt || mine[0].date || '',
                healthScore: 0, churnRisk: 'Unknown',
            };
        };
        const isReal = (v) => v && v !== 'Unknown' && v !== 'Unknown City' && v !== 'Unknown State';

        // 2. Fetch from the database, with a bound that is not hair-trigger.
        //
        // Five seconds was too tight and customers were being shown "Request timed out.
        // Check connection." on a record that was perfectly fine. The first read after a
        // page load also pays for the database connection and the auth handshake, and a
        // well-used customer is a fat node - one real record here is 29 KB, with 55 orders
        // and a 10 KB timeline. On a phone on mobile data that is easily past five
        // seconds, and the customer record then looked broken when it was only slow.
        //
        // Twenty seconds, and one silent retry before anyone is told anything: a blink in
        // the connection should not read as a failure.
        const withTimeout = (promise, ms) => Promise.race([
            promise,
            new Promise((_, reject) => setTimeout(() => reject(new Error('Request timed out. Check connection.')), ms)),
        ]);
        const readCustomer = async () => {
            try {
                return await withTimeout(get(ref(db, `customerBase/${safeKey}`)), 20000);
            } catch (e) {
                if (!/timed out/i.test(e.message)) throw e;
                // The connection is warm by now even if the first attempt was not.
                return withTimeout(get(ref(db, `customerBase/${safeKey}`)), 20000);
            }
        };

        try {
            // Referral/bank extras come from the LEGACY referral DB; awaited in
            // series they held the WHOLE profile hostage — when its index missed
            // (most customers), fallback scans ran and the desk stared at
            // "Loading Profile…" for tens of seconds. The lookup now starts in
            // parallel with the main read and gets 2.5s; past that the profile
            // renders without the extras rather than not at all.
            const referralEarly = CustomerService.getReferralData(normalized).catch(() => null);
            const referralCapped = () => Promise.race([referralEarly, new Promise(r => setTimeout(() => r(null), 2500))]);

            const snapshot = await readCustomer();

            if (snapshot.exists()) {
                const data = snapshot.val();

                // Fetch optional Referral/Bank Data
                // We do this EVEN if internal record exists to get Bank/Referral details
                try {
                    const referralInfo = await referralCapped();
                    if (referralInfo) {
                        data.externalData = referralInfo;
                        // Healing: if internal record is missing name or city, use external
                        if (!data.name || data.name === 'Unknown') data.name = referralInfo.name;
                        if (!data.primaryMobile || data.primaryMobile === '') data.primaryMobile = referralInfo.phone || mobile;
                        if (!data.city || data.city === 'Unknown City') data.city = referralInfo.city || referralInfo.address;
                    }
                } catch (e) {
                    console.warn("Could not fetch referral details for profile", e);
                }

                // Sparse record (website-pushed orders create only a stub). Fill in
                // name/address/city/state/orders/stats from the actual orders.
                const ordersEmpty = !data.orders || (typeof data.orders === 'object' && Object.keys(data.orders).length === 0);
                const needsHeal = ordersEmpty || !isReal(data.name) || (!isReal(data.city) && !isReal(data.address));
                if (needsHeal) {
                    try {
                        const op = await loadOrdersProfile();
                        if (op) {
                            if (!isReal(data.name)) data.name = op.name;
                            if (!isReal(data.city)) data.city = op.city;
                            if (!isReal(data.state)) data.state = op.state;
                            if (!data.address) data.address = op.address;
                            if (!data.pincode) data.pincode = op.pincode;
                            if (ordersEmpty) data.orders = op.orders;
                            if (!data.totalOrders) data.totalOrders = op.totalOrders;
                            if (!data.totalSpent) data.totalSpent = op.totalSpent;
                            if (!data.lastOrderDate) data.lastOrderDate = op.lastOrderDate;
                            if (!data.firstOrderDate) data.firstOrderDate = op.firstOrderDate;
                        }
                    } catch (e) { console.warn('Sparse-record heal failed', e); }
                }

                // Always carry the identity fields (stubs lack them).
                if (!data.primaryMobile) data.primaryMobile = data.mobile || normalized;
                if (!data.id) data.id = safeKey;
                CUSTOMER_CACHE.set(safeKey, data);
                return data;
            }

            // 3. Fallback to Virtual Profile from Referral DB. No customerBase
            // record means the referral data IS the profile, so here the full
            // (already in-flight) lookup is awaited rather than the capped race.
            try {
                const referralInfo = await referralEarly;
                if (referralInfo) {
                    const virtualProfile = {
                        primaryMobile: mobile,
                        name: referralInfo.name || "Unknown",
                        city: referralInfo.city || referralInfo.address || "Unknown",
                        totalSpent: 0,
                        totalOrders: 0,
                        isVirtual: true,
                        externalData: referralInfo,
                        orders: [],
                        healthScore: 0,
                        churnRisk: 'Unknown'
                    };
                    CUSTOMER_CACHE.set(safeKey, virtualProfile);
                    return virtualProfile;
                }
            } catch (e) {
                console.warn("Virtual profile lookup failed", e);
            }

            // 4. Fallback: build a profile from this number's orders (customer exists
            // in orders but was never synced to customerBase). Read-only.
            try {
                const op = await loadOrdersProfile();
                if (op) {
                    op.isUnsynced = true;
                    CUSTOMER_CACHE.set(safeKey, op);
                    return op;
                }
            } catch (e) {
                console.warn("Order-based profile fallback failed", e);
            }

            return null;
        } catch (error) {
            console.error("getCustomer error:", error);
            throw error;
        }
    },

    // Get Total Count — from the maintained counter.
    // This used to download the ENTIRE customerBase tree to count children, on every
    // page change. Its own comment said "revisit past ~50k"; at 111k+ that is tens of
    // megabytes per page turn and was the reason paging crawled. The counter is now
    // maintained by the importer and by every server-side order upsert, so it is
    // authoritative; the full recount is kept only as a self-heal when it is missing.
    getCustomerCount: async () => {
        try {
            const snap = await get(ref(db, 'stats/customerCount'));
            const cached = snap.exists() ? Number(snap.val()) : 0;
            if (cached > 0) return cached;

            const fullSnap = await get(query(ref(db, 'customerBase'), orderByKey()));
            const count = fullSnap.exists() ? fullSnap.size : 0;
            if (count > 0) await update(ref(db, 'stats'), { customerCount: count }).catch(() => {});
            return count;
        } catch (e) {
            console.error('Customer count failed:', e);
            return 0;
        }
    },

    // Fetch Referral & Bank Data from External DB
    getReferralData: async (normalizedMobile) => {
        try {
            // 1. Try to find the Referral Code using uidIndex (mobile -> code)
            let referralCode = null;
            const normalized = normalizedMobile.replace(/\D/g, "").slice(-10); // Ensure 10-digit
            const possibleKeys = [normalized, `+91${normalized}`, `91${normalized}`];

            // One variant failing must not abandon the others. This loop used to let
            // the throw escape, so a miss on `+91…` meant the `91…` key was never
            // tried and a customer indexed under it looked like they had no referral.
            for (const key of possibleKeys) {
                try {
                    const indexSnap = await referralGet(referralRef(`uidIndex/${key}`));
                    if (indexSnap.exists()) {
                        referralCode = indexSnap.val();
                        break;
                    }
                } catch { /* this spelling of the number is not indexed — try the next */ }
            }

            // 2. Fallback: Scan 'users' node by phone if index failed
            if (!referralCode) {
                console.log(`[CustomerService] uidIndex missed for ${normalized}. Attempting fallback scan...`);
                // We'll try both 10-digit and +91 in user records
                for (const phoneVal of [normalized, `+91${normalized}`]) {
                    const fallbackSnap = await referralGet(query(referralRef('users'), orderByChild('phone'), equalTo(phoneVal)));
                    if (fallbackSnap.exists()) {
                        const results = fallbackSnap.val();
                        referralCode = Object.keys(results)[0]; // Use the first matching code
                        console.log(`[CustomerService] Fallback FOUND code via scan: ${referralCode}`);
                        break;
                    }
                }
            }

            // 2. If no index, we might need a manual scan (fallback)
            if (!referralCode) return null;

            // 3. Fetch User Details (Bank, Address, etc)
            const userSnap = await referralGet(referralRef(`users/${referralCode}`));
            if (!userSnap.exists()) return null;

            const userData = userSnap.val();

            // 4. Fetch Referrals made by this person
            // We'll look for logs where referralCode matches
            // (Note: This is O(N) if we fetch all logs, but we can filter if the DB allows)
            const logsSnap = await get(query(ref(db, 'referralLogs'), orderByChild('referralCode'), startAt(referralCode), endAt(referralCode)));
            const referrals = [];
            if (logsSnap.exists()) {
                Object.entries(logsSnap.val()).forEach(([id, log]) => {
                    referrals.push({ ...log, id });
                });
            }

            return {
                ...userData,
                referralCode,
                referrals,
                referralCount: referrals.length
            };

        } catch (error) {
            console.error("getReferralData error:", error);
            return null;
        }
    }
};
